import React, { useState } from 'react';
import { Box, Typography, Card, Alert } from '@mui/material';
import axios from 'axios';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { CustomTextField, CustomButton } from './CustomComponents';
import urbanImage from '../assets/urban_logo.png';
import { emailMessage, requiredMessage } from '../interfaces/Forms';

interface RecoverPasswordForm {
  email: string;
}

function RecoverPassword(): React.ReactElement {
  const [serverMessage, setServerMessage] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const formik = useFormik<RecoverPasswordForm>({
    initialValues: { email: '' },
    validationSchema: object({
      email: string().required(requiredMessage).email(emailMessage),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setServerMessage(null);
      setServerError(null);
      try {
        await axios.post(`${process.env.REACT_APP_API_URL}/recover-password`, {
          email: values.email,
        });
        // Backend should generate a one-time reset link with the user's hashed email
        // and a separate unique token, e.g. /reset-password/<token>/<hashedEmail>,
        // and expire it after 15 minutes.
        setServerMessage(
          'Si existe una cuenta con este correo, te enviaremos un enlace para restablecer tu contraseña.'
        );
        resetForm();
      } catch (error) {
        setServerError(
          'No se pudo enviar el enlace. Verifica tu correo e inténtalo nuevamente más tarde.'
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '100px 20px',
      }}
    >
      <Card
        sx={{
          width: '100%',
          minHeight: '450px',
          maxWidth: '450px',
          padding: '0 40px 40px 40px',
          borderRadius: 4,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          backgroundColor: 'white',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <img
            src={urbanImage}
            alt="Urban Utility Logo"
            style={{ width: '100%', maxWidth: 240, height: 'auto', objectFit: 'contain' }}
          />
        </Box>

        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Recuperar contraseña
          </Typography>
          <Typography variant="body1" sx={{ color: '#535353', lineHeight: 1.7 }}>
            Ingresa el correo asociado a tu cuenta y te enviaremos un enlace para crear una nueva
            contraseña.
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          noValidate
          autoComplete="off"
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <CustomTextField
            fullWidth
            required
            id="email"
            name="email"
            label="Email"
            placeholder="ejemplo@correo.com"
            value={formik.values.email}
            type="email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <CustomButton fullWidth type="submit" disabled={formik.isSubmitting}>
            {formik.isSubmitting ? 'Enviando enlace...' : 'Enviar enlace de recuperación'}
          </CustomButton>

          {serverMessage && (
            <Alert severity="success" sx={{ mb: 1 }}>
              {serverMessage}
            </Alert>
          )}
          {serverError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {serverError}
            </Alert>
          )}
        </Box>
      </Card>
    </Box>
  );
}

export default RecoverPassword;
